import React, { useState } from "react";
import api from "../utils/axios";
import { AnimatedModal } from "./ui";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const PaymentCompletionModal = ({
  isOpen,
  checkoutUrl,
  user,
  organization,
  onLogout,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isRazorpay =
    organization?.payment_gateway === "RAZORPAY" &&
    organization?.razorpaySubscriptionId;

  const handleStripeRedirect = () => {
    if (checkoutUrl) window.location.href = checkoutUrl;
  };

  const handleRazorpayPayment = async () => {
    setLoading(true);
    setError(null);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        setError("Razorpay SDK failed to load. Please refresh and try again.");
        setLoading(false);
        return;
      }

      const options = {
        key: organization.razorpayKeyId,
        subscription_id: organization.razorpaySubscriptionId,
        name: "EzSignly",
        description: "EzSignly Subscription",
        modal: {
          ondismiss: () => setLoading(false),
        },
        handler: async () => {
          try {
            await api.post("/stripe/razorpay/set-payment");
          } catch (e) {
            console.error("set-payment error:", e);
          }
          window.location.reload();
        },
        prefill: { name: user?.name, email: user?.email },
        theme: { color: "#1A6EDB" },
        config: {
          display: {
            blocks: {
              banks: {
                name: "Pay using",
                instruments: [
                  { method: "upi" },
                  { method: "card" },
                  { method: "wallet" },
                ],
              },
            },
            sequence: ["block.banks"],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (response) => {
        setError(
          response?.error?.description || "Payment failed. Please try again.",
        );
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      setError("An unexpected error occurred. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatedModal
      isOpen={isOpen}
      backdropClassName="bg-gray-900/50"
      contentClassName="bg-white rounded-xl w-full max-w-md p-6 shadow-xl relative"
    >
      {onLogout && (
          <div className="absolute top-4 right-4">
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 focus:outline-none transition-colors"
            >
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              <span>Logout</span>
            </button>
          </div>
        )}

        <div className="text-center mb-6">
          <div
            className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${isRazorpay ? "bg-blue-100" : "bg-blue-100"}`}
          >
            {isRazorpay ? (
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            ) : (
              <svg
                className="h-6 w-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
            )}
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Complete Your Payment
          </h3>
          <p className="text-sm text-gray-600">
            {isRazorpay
              ? "Your subscription is ready. Complete the payment via Razorpay to activate your account."
              : "You have a pending payment. Please complete your payment to continue using our services."}
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mt-2">
          {isRazorpay ? (
            <button
              onClick={handleRazorpayPayment}
              disabled={loading}
              className="w-full flex justify-center items-center gap-2 py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <svg
                    className="h-4 w-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Opening Payment…
                </>
              ) : (
                "Pay with Razorpay"
              )}
            </button>
          ) : (
            <button
              onClick={handleStripeRedirect}
              disabled={!checkoutUrl}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Complete Payment
            </button>
          )}
        </div>
    </AnimatedModal>
  );
};


export default PaymentCompletionModal;
